/* InviteCard — invite-a-friend widget for the Profile page.
 *
 * Reads GET /api/referrals/me to fetch the caller's share URL + rolling
 * stats (referrals, coins earned, badge). Copy + Web-Share are both wired.
 * Missing values render silently — no red states when the endpoint returns
 * zero referrals yet.
 */
import { useEffect, useState } from 'react';
import { Share2, Copy, Check, Award } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function InviteCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get('/referrals/me').then(r => setData(r.data)).catch(() => {});
  }, []);

  const copyLink = async () => {
    if (!data?.share_url) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      toast.success('Invite link copied');
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) {
      toast.error('Could not copy — try tapping to select the link.');
    }
  };

  const share = async () => {
    if (!data?.share_url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me on WildQuest',
          text: 'Explore the wild with me — every discovery counts.',
          url: data.share_url,
        });
        return;
      } catch (_e) {
        // user cancelled or share unavailable — fall through
      }
    }
    copyLink();
  };

  if (!data) return null;
  const reward = data.reward_coins_per_referral || 25;

  return (
    <div className="rounded-3xl glass border border-white/10 p-4 space-y-3" data-testid="invite-card">
      <div className="flex items-center gap-2">
        <Share2 className="w-3.5 h-3.5 text-accent" />
        <p className="text-[10px] uppercase tracking-widest text-accent">Bring a friend</p>
        {data.has_badge && (
          <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-[9px] uppercase tracking-widest text-amber-200"
                data-testid="invite-badge">
            <Award className="w-2.5 h-2.5" /> Trail Blazer
          </span>
        )}
      </div>

      <p className="text-sm text-white/85 leading-relaxed">
        Earn <span className="text-accent font-medium">+{reward} WildCoins</span> and the Trail Blazer badge on your first successful invite.
      </p>

      <div className="rounded-2xl bg-black/40 border border-white/10 px-3 py-2 flex items-center gap-2">
        <p className="flex-1 truncate font-mono text-[11px] text-white/70" data-testid="invite-share-url">
          {data.share_url}
        </p>
        <button onClick={copyLink} data-testid="invite-copy-btn"
          className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition">
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-1">
        <button onClick={share} data-testid="invite-share-btn"
          className="h-10 rounded-full bg-accent text-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-1.5">
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
        <div className="h-10 rounded-full bg-white/5 border border-white/10 text-[11px] uppercase tracking-widest flex items-center justify-center gap-2"
             data-testid="invite-stats">
          <span className="text-white/60">Invites</span>
          <span className="font-mono text-accent">{data.count}</span>
          <span className="text-white/30">·</span>
          <span className="font-mono text-accent">+{data.coins_earned}</span>
          <span className="text-white/60">coins</span>
        </div>
      </div>
    </div>
  );
}
